import { Match, Show, Switch } from 'solid-js'
import coinPlain from '~/assets/coin-plain.png'
import coinReward from '~/assets/coin-reward.png'
import rect2975 from '~/assets/rect-2975.svg'
import rect2976 from '~/assets/rect-2976.svg'
import rect2988 from '~/assets/rect-2988.svg'
import rect2989 from '~/assets/rect-2989.svg'
import tickBig from '~/assets/tick-big.svg'
import { ClockIcon } from '~/components/icons/clock'
import { Spinner } from '~/components/spinner'
import { TimeLabel } from '~/components/time'
import { cn } from '~/lib/utils'

type Props = {
  state: 'pending' | 'available' | 'pressing' | 'collected' | 'waiting'
  countdownSeconds?: number
  loading?: boolean
  claiming?: boolean
  onPressStart: () => void
  onPressEnd: () => void
  onClick: (el: HTMLButtonElement, event: MouseEvent) => void
}

function AvailableStripes(props: { animated?: boolean }) {
  return (
    <div
      class={cn(
        'pointer-events-none absolute inset-0',
        props.animated && 'animate-reflection-sweep',
      )}
      aria-hidden="true"
    >
      <div class="absolute left-[41.86px] top-[-70.45px] flex h-[205.895px] w-[126.863px] items-center justify-center">
        <div class="rotate-[-150.14deg] skew-x-[-0.28deg] -scale-y-100">
          <img
            src={rect2975}
            alt=""
            class="block h-[230.793px] w-[12.662px] max-w-none"
          />
        </div>
      </div>
      <div class="absolute left-[-46px] top-[-44px] flex h-[217.868px] w-[146.719px] items-center justify-center">
        <div class="rotate-[-150.14deg] skew-x-[-0.28deg] -scale-y-100">
          <img
            src={rect2976}
            alt=""
            class="block h-[231.787px] w-[34.982px] max-w-none"
          />
        </div>
      </div>
    </div>
  )
}

function TopGloss(props: { img: string }) {
  return (
    <div
      class="pointer-events-none absolute left-[4px] right-[4px] top-[4px] h-[18px]"
      aria-hidden="true"
    >
      <img src={props.img} alt="" class="block h-full w-full" />
    </div>
  )
}

export function DailyBox(props: Props) {
  const effectiveState = () =>
    props.loading ? ('pending' as const) : props.state
  let pressArmed = false

  const handlePressStart = () => {
    pressArmed = true
    props.onPressStart()
  }

  const handlePressEnd = () => {
    if (!pressArmed) return
    pressArmed = false
    props.onPressEnd()
  }

  const handlePressAbort = () => {
    pressArmed = false
  }

  return (
    <Switch>
      <Match when={effectiveState() === 'waiting'}>
        <div
          class={cn(
            'relative flex h-[65px] w-full flex-1 items-center justify-center gap-[10px] rounded-[11px] bg-checkin-pending p-[10px]',
          )}
        >
          <TopGloss img={rect2988} />
          <img
            src={coinReward}
            alt=""
            class="relative h-[45px] w-[45px] opacity-90"
          />
          <span class="absolute left-1/2 top-[56px] flex h-[17px] -translate-x-1/2 items-center justify-center gap-[3px] rounded-[10px] bg-black/30 px-[6px] text-[10px] leading-[14px] text-white backdrop-blur-sm">
            <ClockIcon class="h-[10px] w-[10px]" />
            <TimeLabel
              seconds={props.countdownSeconds ?? 0}
              variant="card"
              class="text-white"
            />
          </span>
        </div>
      </Match>

      <Match when={effectiveState() === 'collected'}>
        <div class="relative flex h-[65px] w-full flex-1 items-center justify-center gap-[10px] overflow-hidden rounded-[11px] bg-checkin-available p-[10px]">
          <TopGloss img={rect2989} />
          <img
            src={coinReward}
            alt=""
            class="relative h-[45px] w-[45px] opacity-30"
          />
          <img
            src={tickBig}
            alt="Collected"
            class="pointer-events-none absolute left-1/2 top-1/2 h-[33.5px] w-[38.285px] -translate-x-1/2 -translate-y-1/2 drop-shadow-[0_2px_6px_rgba(40,205,227,0.35)]"
          />
        </div>
      </Match>

      <Match
        when={
          effectiveState() === 'available' || effectiveState() === 'pressing'
        }
      >
        <button
          type="button"
          disabled={props.claiming}
          onClick={(e) =>
            props.onClick(e.currentTarget as HTMLButtonElement, e)
          }
          onPointerDown={handlePressStart}
          onPointerUp={handlePressEnd}
          onPointerLeave={handlePressAbort}
          onPointerCancel={handlePressAbort}
          class={cn(
            'relative flex h-[65px] w-full flex-1 cursor-pointer items-center justify-center gap-[10px] overflow-hidden rounded-[11px] bg-checkin-available p-[10px] tap-highlight-none transition-transform duration-150 active:scale-[0.97]',
            effectiveState() === 'pressing' && 'scale-[0.97]',
            props.claiming && 'cursor-default bg-[#FEF7CE] active:scale-100',
          )}
        >
          <Show
            when={!props.claiming}
            fallback={
              <Spinner class="size-6 text-[#F8D041]" variant="circle" />
            }
          >
            <TopGloss img={rect2989} />
            <Show when={effectiveState() !== 'pressing'}>
              <AvailableStripes animated />
            </Show>
            <img
              src={coinReward}
              alt=""
              class={cn(
                'relative h-[45px] w-[45px] transition-transform duration-150',
                effectiveState() === 'pressing' && 'scale-95',
              )}
            />
            <Show when={effectiveState() === 'pressing'}>
              <span
                class="pointer-events-none absolute inset-0 bg-white/60"
                aria-hidden="true"
              />
            </Show>
          </Show>
        </button>
      </Match>

      <Match when={effectiveState() === 'pending'}>
        <div
          class={cn(
            'relative flex h-[65px] w-full flex-1 items-center justify-center gap-[10px] overflow-hidden rounded-[11px] bg-checkin-pending p-[10px]',
            props.loading && 'opacity-30',
          )}
        >
          <TopGloss img={rect2988} />
          <img
            src={coinPlain}
            alt=""
            class="relative h-[42px] w-[42px] opacity-30"
          />
        </div>
      </Match>
    </Switch>
  )
}
